import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import {
  useSubscriptionsQuery,
  useSubscriptionPlansQuery,
  useSaveSubscriptionMutation
} from "@queries/website";
import "@styles/opencart-admin.css";

const STATUSES = ["pending", "active", "paused", "cancelled"];

const statusColors = {
  pending: "#b45309",
  active: "#15803d",
  paused: "#6b7280",
  cancelled: "#b91c1c"
};

export default function SubscriptionsPage() {
  const [filters, setFilters] = React.useState({
    status: "",
    plan_id: "",
    q: "",
    date_from: "",
    date_to: ""
  });
  const [page, setPage] = React.useState(1);
  const pageSize = 20;

  // inline editor state: { id, status, next_renewal_date }
  const [editing, setEditing] = React.useState(null);

  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, isError, error } = useSubscriptionsQuery(
    {
      status: filters.status || undefined,
      plan_id: filters.plan_id || undefined,
      q: filters.q || undefined,
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined
    },
    page,
    pageSize
  );

  const { data: plansData } = useSubscriptionPlansQuery({}, 1, 100);
  const plans = plansData?.data || [];

  const saveMutation = useSaveSubscriptionMutation();

  const rows = data?.data || [];
  const total = data?.total ?? data?.meta?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  function openEdit(row) {
    setEditing({
      id: row.subscription_id,
      status: row.status || "pending",
      next_renewal_date: row.next_renewal_date
        ? String(row.next_renewal_date).substring(0, 10)
        : ""
    });
  }

  async function handleSave() {
    try {
      await saveMutation.mutateAsync({
        id: editing.id,
        payload: {
          status: editing.status,
          next_renewal_date: editing.next_renewal_date || null
        }
      });
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ["website", "subscriptions"] });
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to update subscription");
    }
  }

  async function quickStatus(row, status) {
    if (!window.confirm(`Set subscription #${row.subscription_id} to "${status}"?`)) return;
    try {
      await saveMutation.mutateAsync({ id: row.subscription_id, payload: { status } });
      queryClient.invalidateQueries({ queryKey: ["website", "subscriptions"] });
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to update subscription");
    }
  }

  function setFilter(field, value) {
    setFilters((p) => ({ ...p, [field]: value }));
    setPage(1);
  }

  const fmt = (d) => (d ? String(d).substring(0, 10) : "—");

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>Subscriptions</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Website</span>
          <span className="sep">›</span>
          <span>Subscriptions</span>
        </nav>
      </div>

      <div className="oc-card">
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>Customer Subscriptions</strong>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 260px", gap: 16 }}>
          <div>
            {isError && (
              <div style={{ color: "#c00", marginBottom: 8 }}>
                Error: {error?.message || "Unknown error"}
              </div>
            )}

            <table className="oc-table">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>ID</th>
                  <th>Customer</th>
                  <th>Plan</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 120 }}>Next Renewal</th>
                  <th style={{ width: 110 }}>Created</th>
                  <th style={{ width: 80 }}>Order</th>
                  <th style={{ width: 280 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: 24 }}>
                      No subscriptions
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <React.Fragment key={row.subscription_id}>
                    <tr>
                      <td>{row.subscription_id}</td>
                      <td>
                        {row.customer_name || `#${row.customer_id}`}
                        <div style={{ fontSize: 12, color: "#777" }}>
                          {row.customer_email}
                          {row.customer_telephone ? ` · ${row.customer_telephone}` : ""}
                        </div>
                      </td>
                      <td>
                        {row.plan_name || `#${row.plan_id}`}
                        <div style={{ fontSize: 12, color: "#777" }}>
                          {row.plan_price ? `JOD ${Number(row.plan_price).toFixed(2)}` : ""}
                          {row.plan_frequency ? ` / ${row.plan_frequency}` : ""}
                        </div>
                      </td>
                      <td>
                        <span style={{ color: statusColors[row.status] || "#333", fontWeight: 600 }}>
                          {row.status}
                        </span>
                      </td>
                      <td>{fmt(row.next_renewal_date)}</td>
                      <td>{fmt(row.created_at)}</td>
                      <td>
                        {row.order_id ? (
                          <a href={`/orders/${row.order_id}`}>#{row.order_id}</a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {row.status !== "active" && (
                            <button className="oc-btn blue" onClick={() => quickStatus(row, "active")}>
                              activate
                            </button>
                          )}
                          {row.status === "active" && (
                            <button className="oc-btn ghost" onClick={() => quickStatus(row, "paused")}>
                              pause
                            </button>
                          )}
                          {row.status !== "cancelled" && (
                            <button className="oc-btn danger" onClick={() => quickStatus(row, "cancelled")}>
                              cancel
                            </button>
                          )}
                          <button className="oc-btn ghost" onClick={() => openEdit(row)}>
                            edit
                          </button>
                        </div>
                      </td>
                    </tr>
                    {editing?.id === row.subscription_id && (
                      <tr>
                        <td colSpan={8} style={{ background: "#f8fafc" }}>
                          <div
                            style={{ display: "flex", gap: 16, alignItems: "flex-end", padding: 8 }}
                          >
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Status</label>
                              <select
                                className="oc-input"
                                value={editing.status}
                                onChange={(e) =>
                                  setEditing((s) => ({ ...s, status: e.target.value }))
                                }
                              >
                                {STATUSES.map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Next Renewal Date</label>
                              <input
                                type="date"
                                className="oc-input"
                                value={editing.next_renewal_date}
                                onChange={(e) =>
                                  setEditing((s) => ({
                                    ...s,
                                    next_renewal_date: e.target.value
                                  }))
                                }
                              />
                            </div>
                            <button
                              className="oc-btn primary"
                              disabled={saveMutation.isPending}
                              onClick={handleSave}
                            >
                              Save
                            </button>
                            <button className="oc-btn ghost" onClick={() => setEditing(null)}>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="oc-card" style={{ margin: 0 }}>
            <div className="title">Filter</div>
            <div className="form-group">
              <label>Status</label>
              <select
                className="oc-input"
                value={filters.status}
                onChange={(e) => setFilter("status", e.target.value)}
              >
                <option value="">All</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Plan</label>
              <select
                className="oc-input"
                value={filters.plan_id}
                onChange={(e) => setFilter("plan_id", e.target.value)}
              >
                <option value="">All</option>
                {plans.map((p) => (
                  <option key={p.plan_id} value={p.plan_id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Customer (name / email / phone)</label>
              <input
                className="oc-input"
                value={filters.q}
                onChange={(e) => setFilter("q", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>From</label>
              <input
                type="date"
                className="oc-input"
                value={filters.date_from}
                onChange={(e) => setFilter("date_from", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>To</label>
              <input
                type="date"
                className="oc-input"
                value={filters.date_to}
                onChange={(e) => setFilter("date_to", e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className="oc-card"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}
      >
        <div>
          Showing page <strong>{page}</strong> of <strong>{pages}</strong> ({total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="oc-btn ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ‹ Prev
          </button>
          <button className="oc-btn ghost" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
}
